import React from "react";
import { ArrowUpRight } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import {
  Em,
  Eyebrow,
  GhostButton,
  GradientButton,
  Headline,
  Panel,
  Reveal,
  Section,
  SERIF,
  Subhead,
} from "../components/marketing/primitives";
import {
  COMPETITORS,
  CREDENTIALS,
  DIFFERENTIATORS,
  FLOW,
  WHY_NOW,
} from "../components/marketing/content";
import { usePageMeta } from "../components/marketing/usePageMeta";

/**
 * /why-paxley: the full differentiation brief (all twelve points), the
 * cost example, the competitor comparison and who builds it.
 *
 * The comparison describes each vendor only by what it is best known for
 * and puts the difference on Paxley's side; no feature-gap claims about
 * competitors, which change faster than this page.
 *
 * Eyebrows: two (comparison, cost).
 */
export default function WhyPaxley() {
  usePageMeta(
    "Why Paxley | Unify security, protect the supply chain, prove code trust",
    "Twelve reasons teams choose Paxley over a stack of per-seat AppSec tools: supply chain first, Code Trust evidence, source-code control, consolidation and up to 90% lower cost."
  );
  return (
    <MarketingLayout>
      <HeroSection />
      <TwelveSection />
      <CostSection />
      <ComparisonSection />
      <TeamSection />
      <FinalCTA />
    </MarketingLayout>
  );
}

function HeroSection() {
  return (
    <Section padding="hero" container="narrow">
      <div className="text-center">
        <Headline size="hero" className="mx-auto">
          Today&rsquo;s problems, <Em>not yesterday&rsquo;s tools.</Em>
        </Headline>
        <Subhead size="large" className="mx-auto mt-6 max-w-xl">
          Paxley unifies proven security engines into one developer-friendly, evidence-driven
          platform for code, the supply chain and Code Trust.
        </Subhead>
      </div>
      <Reveal>
        <ol className="mx-auto mt-12 grid max-w-4xl gap-px overflow-hidden rounded-2xl border border-[#E4DFD3] bg-[#E4DFD3] sm:grid-cols-5">
          {FLOW.map((step, i) => (
            <li key={step} className="bg-white px-4 py-4">
              <span className="font-mono text-xs opacity-45">0{i + 1}</span>
              <p className="mt-1.5 text-sm font-semibold leading-snug">{step}</p>
            </li>
          ))}
        </ol>
      </Reveal>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// The twelve: numbered grid on hairlines, three across on wide screens
// ──────────────────────────────────────────────────────────────────

function TwelveSection() {
  return (
    <Section padding="tight">
      <div className="grid gap-x-10 sm:grid-cols-2 lg:grid-cols-3">
        {DIFFERENTIATORS.map((d, i) => (
          <Reveal key={d.n} delay={0.03 * (i % 3)}>
            <div className="h-full border-t border-[#E4DFD3] py-7">
              <span className="text-3xl opacity-30" style={SERIF}>
                {d.n}
              </span>
              <h2 className="mt-2 text-lg font-semibold">{d.title}</h2>
              <p className="mt-2 text-sm leading-relaxed opacity-75">{d.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Cost: green panel, the worked example
// ──────────────────────────────────────────────────────────────────

function CostSection() {
  return (
    <Section container="none" padding="tight">
      <Panel tone="green">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Eyebrow className="!opacity-70">The cost</Eyebrow>
              <Headline size="section" className="mt-4">
                Up to 90% less. <Em>Here&rsquo;s the maths.</Em>
              </Headline>
              <Subhead className="mt-4 !opacity-80">
                Per-seat tools charge for every developer, on every tool. Paxley charges for the
                products and repositories you protect.
              </Subhead>
            </Reveal>
            <Reveal delay={0.08} className="lg:col-span-7">
              <p className="text-sm opacity-70">Example: 50 developers working across 10 repositories</p>
              <dl className="mt-5 space-y-4">
                <div className="rounded-2xl bg-white/10 p-5">
                  <dt className="text-sm opacity-75">Per-seat tool stack (SAST, SCA, secrets)</dt>
                  <dd className="mt-1 flex items-baseline gap-2">
                    <span className="text-4xl" style={SERIF}>
                      ~$4,750
                    </span>
                    <span className="text-sm opacity-60">a month, about $95 per developer</span>
                  </dd>
                </div>
                <div className="rounded-2xl bg-[#F5F1E6] p-5 text-[#17150F]">
                  <dt className="text-sm opacity-70">Paxley Growth, everything included</dt>
                  <dd className="mt-1 flex items-baseline gap-2">
                    <span className="text-4xl" style={SERIF}>
                      $499
                    </span>
                    <span className="text-sm opacity-60">a month, about 89% less</span>
                  </dd>
                </div>
              </dl>
              <p className="mt-4 text-xs leading-relaxed opacity-55">
                Per-seat figure is an estimate for a typical combination of per-developer AppSec
                tools; your quotes will vary. Paxley prices are current market-entry prices.
              </p>
            </Reveal>
          </div>
        </div>
      </Panel>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Comparison: a plain table, Paxley's difference in the right column
// ──────────────────────────────────────────────────────────────────

function ComparisonSection() {
  return (
    <Section>
      <Reveal className="mx-auto max-w-3xl text-center">
        <Eyebrow>How we compare</Eyebrow>
        <Headline size="section" className="mt-4">
          One platform where you&rsquo;d <Em>buy five.</Em>
        </Headline>
      </Reveal>

      <div className="mt-12 overflow-hidden rounded-2xl border border-[#E4DFD3] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-[#E4DFD3] bg-[#FBF8F1] text-left">
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-widest opacity-50">Tool</th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-widest opacity-50">Best known for</th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-widest text-[#1E4D38]">
                  Where Paxley differs
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPETITORS.map((c) => (
                <tr key={c.name} className="border-t border-[#E4DFD3]/70 align-top">
                  <td className="whitespace-nowrap px-6 py-4 font-semibold">{c.name}</td>
                  <td className="px-6 py-4 opacity-65">{c.knownFor}</td>
                  <td className="px-6 py-4">{c.paxley}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mx-auto mt-4 max-w-3xl text-center text-xs opacity-50">
        Vendors are described by their best-known focus; most sell further modules. Product names
        are trademarks of their owners.
      </p>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Team and the market facts behind the timing
// ──────────────────────────────────────────────────────────────────

function TeamSection() {
  return (
    <Section container="none" padding="tight">
      <Panel tone="coal">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <Headline size="section">
                Practitioner-built, <Em>at the right time.</Em>
              </Headline>
              <p className="mt-6 text-base leading-relaxed opacity-75">
                Paxley comes from Nucleus Systems, which has used IBM AppScan, Veracode, Mend,
                Black Duck, Snyk, Checkmarx, SonarQube, JFrog and more than 20 open-source tools in
                client delivery. It is led by Godfrey Kutumela, formerly of the IBM application
                security product development team.
              </p>
              <dl className="mt-8 grid gap-6 sm:grid-cols-2">
                {CREDENTIALS.map((c) => (
                  <div key={c.k} className="border-t border-white/15 pt-4">
                    <dt className="text-2xl" style={SERIF}>
                      {c.k}
                    </dt>
                    <dd className="mt-1 text-sm leading-relaxed opacity-60">{c.v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
            <Reveal delay={0.08}>
              <ul>
                {WHY_NOW.map((f) => (
                  <li key={f.figure} className="border-t border-white/15 py-5 first:border-t-0 first:pt-0">
                    <p className="text-3xl" style={SERIF}>
                      {f.figure}
                    </p>
                    <p className="mt-1 font-medium">{f.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed opacity-65">{f.body}</p>
                    <a
                      href={f.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs opacity-50 underline-offset-2 hover:underline hover:opacity-90"
                    >
                      {f.source.label}
                      <ArrowUpRight className="h-3 w-3" />
                    </a>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </Panel>
    </Section>
  );
}

function FinalCTA() {
  return (
    <Section container="narrow">
      <Reveal className="text-center">
        <Headline size="section">
          See the difference <Em>on your own code.</Em>
        </Headline>
        <Subhead className="mx-auto mt-4 max-w-xl">
          Start with a free scan, or let us run a 30-day guided trial with your team.
        </Subhead>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GradientButton to="/onboarding" size="lg">
            Start free scan
          </GradientButton>
          <GhostButton to="/inquiry?interest=trial" size="lg">
            Request a guided trial
          </GhostButton>
        </div>
      </Reveal>
    </Section>
  );
}
